import { ReactElement, useState } from 'react'
import {
  GetStaticPathsContext,
  GetStaticPropsContext,
  InferGetStaticPropsType,
  NextPageContext
} from 'next'

import Meta from '@/components/base/Meta'
import PasswordForm from '@/components/base/PasswordForm'
import Layout from '@/components/layout/Layout'
import PageBuilder from '@/components/pages/PageBuilder'
import usePagePrecache from '@/hooks/usePagePrecache'
import { getPagePrecacheMedia } from '@/utils/page'
import { mapPageStaticProps } from '@/utils/pageData'
import { getPromiseFromFetch } from '@/utils/request'
import { API } from '@/constants'

import { WPPageData, WPRoutes } from '@/types'

export default function Page(
  pageData: InferGetStaticPropsType<typeof getStaticProps>
) {
  const [password, setPassword] = useState('')
  const [isAuthenticated, setIsAuthenticated] = useState(!pageData.is_protected)
  const [error, setError] = useState('')

  const handlePasswordSubmit = (password: string): boolean => {
    if (password === pageData.password) {
      setIsAuthenticated(true)
      setError('')
      return true
    } else {
      setError('Password errata')
      return false
    }
  }

  usePagePrecache(true, getPagePrecacheMedia(pageData))

  if (pageData.is_protected && !isAuthenticated) {
    return (
      <PasswordForm
        onSubmit={handlePasswordSubmit}
        password={password}
        setPassword={setPassword}
        error={error}
        setError={setError}
      />
    )
  }
  return <PageBuilder pageData={pageData} />
}

Page.getLayout = function getLayout(
  page: ReactElement,
  props: NextPageContext
) {
  return (
    <Layout pageData={props}>
      <Meta pageData={props} />
      {page}
    </Layout>
  )
}

export async function getStaticProps({
  params,
  locale
}: GetStaticPropsContext<{ routes: string[] }>) {
  const res = await getPromiseFromFetch<WPPageData>({
    method: 'get',
    url: `/${locale}/${params?.routes.join('/')}/`
  })

  if (locale === 'default' || res.status === 404) return { notFound: true }

  const { data: pageData } = res

  return {
    props: {
      ...(await mapPageStaticProps(pageData, locale))
    }
  }
}

export async function getStaticPaths({}: GetStaticPathsContext) {
  const { data: routes } = await getPromiseFromFetch<WPRoutes>({
    method: 'get',
    url: API.ROUTES
  })

  return {
    paths: routes
      .filter((r) => r.routes.length > 0)
      .map((route) => ({
        params: {
          routes: route.routes.filter((item) => item !== route.locale)
        },
        locale: route.locale
      })),
    fallback: 'blocking' // false
  }
}
